import * as React from "react";
import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Field } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/track";
import { TimezoneSelect } from "@/components/timezone-select";
import { MeetingLinkField } from "@/components/meeting-link-field";
import { browserTimeZone } from "@/lib/timezones";
import { getMyProfile, getMyOwnedTeam, updateMyProfile, type Profile } from "@/lib/account.functions";
import {
  getMyPurchaseHistory,
  getStripeReceiptUrl,
  type PurchaseHistoryRow,
} from "@/lib/billing.functions";
import {
  getMySessions,
  revokeSession,
  listConnectedAssistants,
  type SessionRow,
  type ConnectedAssistantRow,
} from "@/lib/security.functions";
import {
  getOutlookConnection,
  startOutlookConnect,
  disconnectOutlook,
} from "@/lib/ms-connect.functions";
import {
  getMyGmailConnection,
  startGmailConnect,
  disconnectGmail,
} from "@/lib/google-connect.functions";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DEFAULT_SIGNATURE_TEMPLATE,
  SIGNATURE_TOKEN_NAMES,
  renderSignature,
} from "@/lib/signature";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import {
  getMyEmailPreferences,
  updateMyEmailPreferences,
} from "@/lib/email-preferences.functions";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "signature", label: "Signature" },
  { id: "email", label: "Email" },
  { id: "billing", label: "Billing" },
  { id: "connections", label: "App Connections" },
  { id: "security", label: "Security" },
] as const;
type TabId = (typeof TABS)[number]["id"];

const searchSchema = z.object({
  tab: z.enum(["profile", "signature", "email", "billing", "connections", "security"]).optional(),
});

export const Route = createFileRoute("/_authed/profile")({
  head: () => ({
    meta: [
      { title: "Profile: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: searchSchema,
  component: ProfilePage,
});

function ProfilePage() {
  const search = useSearch({ from: "/_authed/profile" });
  const navigate = useNavigate();
  const activeTab: TabId = search.tab ?? "profile";
  const setTab = (id: TabId) =>
    navigate({
      to: "/profile",
      search: { tab: id === "profile" ? undefined : id },
      replace: true,
    });

  return (
    <main className="mx-auto max-w-[640px] px-4 pb-16 pt-6">
      <header className="mb-5">
        <p className="font-serif text-[16px] font-semibold italic text-brand-accent">Settings</p>
        <h1 className="mt-1 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Your account.
        </h1>
      </header>

      <nav aria-label="Settings tabs" className="mb-6 flex gap-1 border-b border-brand-line">
        {TABS.map((tab) => {
          const active = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(tab.id)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-[14px] font-medium transition-colors",
                active
                  ? "border-brand-ink text-brand-ink"
                  : "border-transparent text-brand-muted/70 hover:text-brand-ink",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === "profile" && <ProfilePanel />}
      {activeTab === "signature" && <SignaturePanel />}
      {activeTab === "email" && <EmailPanel />}
      {activeTab === "billing" && <BillingPanel />}
      {activeTab === "connections" && <ConnectionsPanel />}
      {activeTab === "security" && <SecurityPanel />}
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Profile (existing form)
// ─────────────────────────────────────────────────────────────────────────────

type FormState = {
  first_name: string;
  last_name: string;
  title: string;
  firm: string;
  phone: string;
  linkedin_url: string;
  team_name: string;
  timezone: string;
  meeting_link_kind: "zoom_personal" | "custom" | "teams_auto" | "google_meet_auto" | "booking_page";
  meeting_link_url: string;
  email_alerts_meetings: boolean;
  work_hours_start: string;
  work_hours_end: string;
  work_days: number[];
};
const EMPTY: FormState = { first_name: "", last_name: "", title: "", firm: "", phone: "", linkedin_url: "", team_name: "", timezone: "", meeting_link_kind: "zoom_personal", meeting_link_url: "", email_alerts_meetings: true, work_hours_start: "09:00", work_hours_end: "17:00", work_days: [1, 2, 3, 4, 5] };

function ProfilePanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fetchProfile = useServerFn(getMyProfile);
  const saveProfile = useServerFn(updateMyProfile);
  const fetchOwnedTeam = useServerFn(getMyOwnedTeam);

  const profileQuery = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: () => fetchProfile(),
    enabled: !!user,
  });

  const ownedTeamQuery = useQuery({
    queryKey: ["my-owned-team", user?.id],
    queryFn: () => fetchOwnedTeam(),
    enabled: !!user,
  });
  const ownsTeam = !!ownedTeamQuery.data;

  // The Teams choice needs an Outlook account that also gave calendar permission.
  const fetchOutlook = useServerFn(getOutlookConnection);
  const outlookQuery = useQuery({
    queryKey: ["my-outlook-connection"],
    queryFn: () => fetchOutlook(),
    enabled: !!user,
  });
  const teamsAvailable =
    !!outlookQuery.data && outlookQuery.data.connected && outlookQuery.data.calendar_ready;
  const fetchGmail = useServerFn(getMyGmailConnection);
  const gmailQuery = useQuery({
    queryKey: ["my-gmail-connection"],
    queryFn: () => fetchGmail(),
    enabled: !!user,
  });
  const googleAvailable =
    !!gmailQuery.data && gmailQuery.data.connected && gmailQuery.data.calendar_ready;

  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [status, setStatus] = React.useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  React.useEffect(() => {
    if (!profileQuery.data) return;
    setForm((prev) => ({
      first_name: profileQuery.data.first_name ?? "",
      last_name: profileQuery.data.last_name ?? "",
      title: profileQuery.data.title ?? "",
      firm: profileQuery.data.firm ?? "",
      phone: profileQuery.data.phone ?? "",
      linkedin_url: profileQuery.data.linkedin_url ?? "",
      team_name: prev.team_name,
      timezone: profileQuery.data.timezone ?? prev.timezone ?? "",
      meeting_link_kind:
        profileQuery.data.meeting_link_kind === "custom"
          ? "custom"
          : profileQuery.data.meeting_link_kind === "teams_auto"
            ? "teams_auto"
            : profileQuery.data.meeting_link_kind === "google_meet_auto"
              ? "google_meet_auto"
            : profileQuery.data.meeting_link_kind === "booking_page"
              ? "booking_page"
            : "zoom_personal",
      meeting_link_url: profileQuery.data.meeting_link_url ?? "",
      email_alerts_meetings: profileQuery.data.email_alerts_meetings !== false,
      work_hours_start: (profileQuery.data.work_hours_start ?? "09:00").slice(0, 5),
      work_hours_end: (profileQuery.data.work_hours_end ?? "17:00").slice(0, 5),
      work_days: profileQuery.data.work_days?.length ? profileQuery.data.work_days : [1, 2, 3, 4, 5],
    }));
  }, [profileQuery.data]);

  React.useEffect(() => {
    if (!ownedTeamQuery.data) return;
    setForm((prev) => ({ ...prev, team_name: ownedTeamQuery.data?.name ?? "" }));
  }, [ownedTeamQuery.data]);

  const mutation = useMutation({
    mutationFn: (data: FormState) => {
      const payload: Record<string, string | boolean | number[] | undefined> = {
        email_alerts_meetings: data.email_alerts_meetings,
        first_name: data.first_name,
        last_name: data.last_name,
        title: data.title,
        firm: data.firm,
        phone: data.phone,
        linkedin_url: data.linkedin_url,
        timezone: data.timezone || undefined,
        meeting_link_kind:
          data.meeting_link_kind === "teams_auto" ||
          data.meeting_link_kind === "google_meet_auto" ||
          data.meeting_link_kind === "booking_page" ||
          data.meeting_link_url.trim()
            ? data.meeting_link_kind
            : undefined,
        // Keep the saved personal link while Teams is selected. It is inactive,
        // not deleted, and returns when the user switches back.
        meeting_link_url: data.meeting_link_url.trim(),
        work_hours_start: data.work_hours_start,
        work_hours_end: data.work_hours_end,
        work_days: data.work_days,
      };
      if (ownsTeam) payload.team_name = data.team_name.trim();
      return saveProfile({ data: payload });
    },
    onSuccess: () => {
      setStatus({ kind: "ok", msg: "Saved." });
      track("account_settings_changed", { section: "profile" });
      queryClient.invalidateQueries({ queryKey: ["my-profile", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["my-owned-team", user?.id] });
    },
    onError: (err: Error) =>
      setStatus({ kind: "err", msg: err.message || "Could not save changes." }),
  });

  // Preselect the browser's zone when the profile carries none yet.
  React.useEffect(() => {
    if (!profileQuery.data) return;
    if (profileQuery.data.timezone) return;
    setForm((f) => (f.timezone ? f : { ...f, timezone: browserTimeZone() }));
  }, [profileQuery.data]);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setStatus(null);
  };

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    if (ownsTeam) {
      const t = form.team_name.trim();
      if (t.length < 1 || t.length > 50) {
        setStatus({ kind: "err", msg: "Team name must be 1–50 characters." });
        return;
      }
    }
    mutation.mutate(form);
  }

  const email = user?.email ?? "";

  return (
    <section>
      <PanelHeader
        title="Profile"
        subtitle="How you appear in outreach drafts and invoices."
      />
      <form
        onSubmit={onSubmit}
        className="mt-4 rounded-xl border border-brand-line bg-brand-paper px-4 py-1"
      >
        <Field label="First name" value={form.first_name} onChange={set("first_name")} />
        <Field label="Last name" value={form.last_name} onChange={set("last_name")} />
        <Field
          label="Title"
          value={form.title}
          onChange={set("title")}
          placeholder="Managing Partner"
        />
        <Field label="Firm" value={form.firm} onChange={set("firm")} />
        <Field label="Phone" type="tel" value={form.phone} onChange={set("phone")} />
        <Field
          label="LinkedIn address"
          value={form.linkedin_url}
          onChange={set("linkedin_url")}
          placeholder="https://www.linkedin.com/in/"
        />
        <TimezoneSelect
          value={form.timezone}
          onChange={(tz) => {
            setForm((f) => ({ ...f, timezone: tz }));
            setStatus(null);
          }}
          hint="Used for AgentDart check times, meeting times and email alerts."
        />
        <MeetingLinkField
          teamsAvailable={teamsAvailable}
          googleAvailable={googleAvailable}
          kind={form.meeting_link_kind}
          url={form.meeting_link_url}
          onChange={(next) => {
            setForm((f) => ({ ...f, ...next }));
            setStatus(null);
          }}
        />
        <fieldset className="border-t border-brand-line py-4">
          <legend className="text-sm font-medium text-brand-ink">Availability</legend>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="Start" type="time" value={form.work_hours_start} onChange={set("work_hours_start")} />
            <Field label="End" type="time" value={form.work_hours_end} onChange={set("work_hours_end")} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Working days">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => {
              const selected = form.work_days.includes(index);
              return <Button key={day} type="button" variant={selected ? "default" : "outline"} size="sm" aria-pressed={selected} onClick={() => setForm((current) => ({ ...current, work_days: selected ? current.work_days.filter((value) => value !== index) : [...current.work_days, index].sort() }))}>{day}</Button>;
            })}
          </div>
          <p className="mt-3 text-xs text-brand-muted">AgentDart only agrees to meeting times inside these hours. Times you set yourself are not restricted.</p>
        </fieldset>
        <label className="flex items-start gap-3 border-t border-brand-line py-4 text-sm text-brand-ink">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-brand-gold"
            checked={form.email_alerts_meetings}
            onChange={(e) => {
              const checked = e.target.checked;
              setForm((f) => ({ ...f, email_alerts_meetings: checked }));
              setStatus(null);
            }}
          />
          <span>
            Email me when a meeting is confirmed and on the morning of the meeting.
          </span>
        </label>
        {ownsTeam && (
          <Field
            label="Team name"
            value={form.team_name}
            onChange={set("team_name")}
            maxLength={50}
            hint="Shown on your Team page. 1–50 characters."
          />
        )}
        <Field
          label="Work email"
          type="email"
          value={email}
          readOnly
          hint="Used to sign in. Contact support to change."
        />
        <StatusMessage status={status} />
        <div className="py-4">
          <Button type="submit" disabled={mutation.isPending} className="w-full">
            {mutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Signature
// ─────────────────────────────────────────────────────────────────────────────

function SignaturePanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fetchProfile = useServerFn(getMyProfile);
  const saveProfile = useServerFn(updateMyProfile);

  const profileQuery = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: () => fetchProfile(),
    enabled: !!user,
  });

  const [text, setText] = React.useState<string>("");
  const [status, setStatus] = React.useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!profileQuery.data || initialized) return;
    setText(profileQuery.data.signature_text ?? DEFAULT_SIGNATURE_TEMPLATE);
    setInitialized(true);
  }, [profileQuery.data, initialized]);

  const mutation = useMutation({
    mutationFn: (value: string) => saveProfile({ data: { signature_text: value } }),
    onSuccess: () => {
      setStatus({ kind: "ok", msg: "Signature saved." });
      track("account_settings_changed", { section: "signature" });
      queryClient.invalidateQueries({ queryKey: ["my-profile", user?.id] });
    },
    onError: (err: Error) =>
      setStatus({ kind: "err", msg: err.message || "Could not save." }),
  });

  const profile: Profile | undefined = profileQuery.data;
  const preview = renderSignature(text, {
    first_name: profile?.first_name,
    last_name: profile?.last_name,
    title: profile?.title,
    firm: profile?.firm,
    phone: profile?.phone,
    linkedin_url: profile?.linkedin_url,
    email: user?.email ?? null,
  });

  return (
    <section>
      <PanelHeader
        title="Signature"
        subtitle="Appended to every outreach email draft. LinkedIn messages are sent without a signature."
      />

      <div className="mt-4 rounded-xl border border-brand-line bg-brand-paper p-4">
        <label className="block text-[13px] font-semibold uppercase tracking-[0.18em] text-brand-ink">
          Template
        </label>
        <p className="mt-1 text-[14px] text-brand-muted">
          Available tokens:{" "}
          {SIGNATURE_TOKEN_NAMES.map((t, i) => (
            <React.Fragment key={t}>
              <code className="rounded bg-brand-ink/5 px-1 py-0.5 text-[13px]">{`{{${t}}}`}</code>
              {i < SIGNATURE_TOKEN_NAMES.length - 1 ? " " : ""}
            </React.Fragment>
          ))}
        </p>
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setStatus(null);
          }}
          rows={8}
          maxLength={2000}
          className="mt-3 w-full rounded-md border border-brand-line bg-white px-3 py-2 font-mono text-[14px] leading-relaxed text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
        />
        <div className="mt-2 flex items-center justify-between">
          <button
            type="button"
            className="text-[12px] text-brand-muted underline-offset-2 hover:underline"
            onClick={() => {
              setText(DEFAULT_SIGNATURE_TEMPLATE);
              setStatus(null);
            }}
          >
            Reset to default
          </button>
          <span className="text-[13px] text-brand-muted">{text.length} / 2000</span>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-brand-line bg-brand-paper p-4">
        <h3 className="text-[13px] font-semibold uppercase tracking-[0.18em] text-brand-ink">
          Preview
        </h3>
        <pre className="mt-2 whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-brand-ink">
          {preview || (
            <span className="text-brand-muted italic">
              Empty signature — your profile fields are blank.
            </span>
          )}
        </pre>
      </div>

      <StatusMessage status={status} />

      <Button
        onClick={() => mutation.mutate(text)}
        disabled={mutation.isPending}
        className="mt-4 w-full"
      >
        {mutation.isPending ? "Saving…" : "Save signature"}
      </Button>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Billing
// ─────────────────────────────────────────────────────────────────────────────

function formatCurrency(cents: number | null, currency: string | null): string {
  if (cents === null) return "—";
  const code = (currency ?? "usd").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${code}`;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function BillingPanel() {
  const fetchHistory = useServerFn(getMyPurchaseHistory);
  const fetchReceipt = useServerFn(getStripeReceiptUrl);
  const query = useQuery({
    queryKey: ["my-purchases"],
    queryFn: () => fetchHistory(),
  });

  const [loadingReceipt, setLoadingReceipt] = React.useState<string | null>(null);
  const [receiptError, setReceiptError] = React.useState<string | null>(null);
  const receiptCache = React.useRef<Map<string, string>>(new Map());

  async function openReceipt(row: PurchaseHistoryRow) {
    if (!row.sessionId) return;
    setReceiptError(null);
    const cached = receiptCache.current.get(row.sessionId);
    if (cached) {
      window.open(cached, "_blank", "noopener,noreferrer");
      return;
    }
    setLoadingReceipt(row.id);
    try {
      const res = await fetchReceipt({ data: { sessionId: row.sessionId } });
      if (!res.url) {
        setReceiptError("Stripe did not return a receipt URL for this purchase.");
        return;
      }
      receiptCache.current.set(row.sessionId, res.url);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setReceiptError(err instanceof Error ? err.message : "Could not fetch receipt.");
    } finally {
      setLoadingReceipt(null);
    }
  }

  return (
    <section>
      <PanelHeader
        title="Billing"
        subtitle="Purchases made through CapitalDart checkout."
      />

      {query.isLoading ? (
        <div className="mt-4 rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center text-[14px] text-brand-muted">
          Loading…
        </div>
      ) : query.error ? (
        <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-4 text-[14px] text-destructive">
          {(query.error as Error).message}
        </div>
      ) : (query.data ?? []).length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center text-[14px] text-brand-muted">
          No purchases yet. Top up at{" "}
          <a href="/buy-credits" className="underline">/buy-credits</a>.
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-xl border border-brand-line bg-brand-paper">
          <table className="w-full text-[14px]">
            <thead className="bg-brand-ink/[0.03] text-left text-[12px] uppercase tracking-wider text-brand-muted">
              <tr>
                <th className="px-3 py-2 font-semibold">Date</th>
                <th className="px-3 py-2 font-semibold">Product</th>
                <th className="px-3 py-2 text-right font-semibold">Credits</th>
                <th className="px-3 py-2 text-right font-semibold">Deals</th>
                <th className="px-3 py-2 text-right font-semibold">Amount</th>
                <th className="px-3 py-2 text-right font-semibold">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {(query.data ?? []).map((row) => (
                <tr key={row.id} className="border-t border-brand-line">
                  <td className="px-3 py-2 text-brand-ink">{formatDate(row.date)}</td>
                  <td className="px-3 py-2 text-brand-ink">{row.productLabel}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-brand-ink">
                    {row.credits || "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-brand-ink">
                    {row.runs || "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-brand-ink">
                    {formatCurrency(row.amountCents, row.currency)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.sessionId ? (
                      <button
                        type="button"
                        onClick={() => openReceipt(row)}
                        disabled={loadingReceipt === row.id}
                        className="text-brand-accent underline-offset-2 hover:underline disabled:opacity-50"
                      >
                        {loadingReceipt === row.id ? "Loading…" : "View"}
                      </button>
                    ) : (
                      <span className="text-brand-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {receiptError && (
        <p className="mt-3 text-[14px] text-destructive">{receiptError}</p>
      )}

      <p className="mt-4 text-[14px] text-brand-muted">
        Card details are collected by Stripe at checkout. To change your card,
        start a new purchase at{" "}
        <a href="/buy-credits" className="underline">/buy-credits</a> — Stripe
        will prompt for new payment details.
      </p>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Security
// ─────────────────────────────────────────────────────────────────────────────

function parseUserAgent(ua: string | null): string {
  if (!ua) return "Unknown device";
  const s = ua;
  let browser = "Browser";
  if (/Edg\//.test(s)) browser = "Edge";
  else if (/Chrome\//.test(s) && !/Chromium/.test(s)) browser = "Chrome";
  else if (/Firefox\//.test(s)) browser = "Firefox";
  else if (/Safari\//.test(s) && !/Chrome/.test(s)) browser = "Safari";
  let os = "Unknown OS";
  if (/Windows NT/.test(s)) os = "Windows";
  else if (/Mac OS X/.test(s)) os = "macOS";
  else if (/Android/.test(s)) os = "Android";
  else if (/iPhone|iPad|iOS/.test(s)) os = "iOS";
  else if (/Linux/.test(s)) os = "Linux";
  return `${browser} · ${os}`;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (Number.isNaN(then)) return iso;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ConnectionsPanel() {
  return (
    <section className="space-y-8">
      <ConnectedAssistantsList />
      <ConnectedAccountsCard />
    </section>
  );
}

function SecurityPanel() {
  return (
    <section className="space-y-8">
      <PasswordChangeForm />
      <SessionsList />
      <div className="rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-4">
        <h3 className="text-[13px] font-semibold uppercase tracking-[0.18em] text-brand-ink">
          Two-factor authentication
        </h3>
        <p className="mt-1 text-[14px] text-brand-muted">Coming soon.</p>
      </div>
    </section>
  );
}

function ConnectedAccountsCard() {
  const fetchConnection = useServerFn(getOutlookConnection);
  const startConnect = useServerFn(startOutlookConnect);
  const disconnect = useServerFn(disconnectOutlook);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["my-outlook-connection"],
    queryFn: () => fetchConnection(),
  });
  const [error, setError] = React.useState<string | null>(null);
  const [connecting, setConnecting] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);

  async function onConnect() {
    setError(null);
    setConnecting(true);
    try {
      const { authorize_url } = await startConnect();
      const target = window.top ?? window;
      target.location.href = authorize_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the Outlook connection.");
      setConnecting(false);
    }
  }

  async function onConfirmDisconnect() {
    setError(null);
    setDisconnecting(true);
    try {
      await disconnect();
      await queryClient.invalidateQueries({ queryKey: ["my-outlook-connection"] });
      await queryClient.invalidateQueries({ queryKey: ["mailbox-readiness"] });
      setConfirmOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not disconnect Outlook.");
    } finally {
      setDisconnecting(false);
    }
  }

  const conn = query.data;

  return (
    <div>
      <PanelHeader
        title="Connected accounts"
        subtitle="Email accounts linked to your CapitalDart account."
      />
      {query.isLoading ? (
        <div className="mt-4 rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center text-[14px] text-brand-muted">
          Loading…
        </div>
      ) : query.error ? (
        <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-4 text-[14px] text-destructive">
          {(query.error as Error).message}
        </div>
      ) : conn && conn.connected ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-brand-line bg-brand-paper px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-[14px] font-medium text-brand-ink">
              Outlook · {conn.account_email}
            </div>
            <div className="mt-0.5 text-[13px] text-brand-muted">
              Connected {formatConnectedDate(conn.connected_at)}
            </div>
            <div className="mt-1 text-[13px] text-brand-muted">
              Connect Outlook to send outreach from your own address. CapitalDart
              saves drafts to your Outlook Drafts folder, sends only when you press
              Send or give AgentDart permission, and reads replies to outreach you
              sent through CapitalDart so your pipeline updates itself. It does not
              read other mail. Reading replies to CapitalDart outreach. Disconnect to
              stop.
            </div>
            {!conn.calendar_ready && (
              <div className="mt-2 text-[13px] text-brand-ink">
                Reconnect Outlook to let AgentDart create Teams meetings.
              </div>
            )}


          </div>
          <div className="flex shrink-0 items-center gap-3">
            {!conn.calendar_ready && (
              <button
                type="button"
                onClick={() => void onConnect()}
                disabled={connecting}
                className="text-[14px] text-brand-accent underline-offset-2 hover:underline"
              >
                {connecting ? "Opening Microsoft…" : "Reconnect"}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setError(null);
                setConfirmOpen(true);
              }}
              className="text-[14px] text-brand-accent underline-offset-2 hover:underline"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-brand-line bg-brand-paper px-4 py-4">
          <p className="text-[14px] text-brand-muted">
            Connect Outlook to send outreach from your own address. CapitalDart
            saves drafts to your Outlook Drafts folder, sends only when you press
            Send or give AgentDart permission, and reads replies to outreach you
            sent through CapitalDart so your pipeline updates itself. It does not
            read other mail.
          </p>


          <div className="mt-4">
            <Button onClick={() => void onConnect()} disabled={connecting}>
              {connecting ? "Opening Microsoft…" : "Connect Outlook"}
            </Button>
          </div>
        </div>
      )}
      {error && <p className="mt-3 text-[14px] text-destructive">{error}</p>}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open && !disconnecting) setConfirmOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Outlook?</AlertDialogTitle>
            <AlertDialogDescription>
              CapitalDart will no longer be able to place drafts in your Outlook
              Drafts folder. Drafts already saved there stay in place. You can
              connect again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void onConfirmDisconnect();
              }}
              disabled={disconnecting}
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GmailConnectionRow />

      <div className="mt-4 rounded-xl border border-brand-line bg-brand-paper px-4 py-4">
        <div className="text-[14px] font-medium text-brand-ink">What we read</div>
        <p className="mt-2 text-[13px] text-brand-muted">
          Replies matched to contacts on your deals only.
        </p>
        <p className="mt-1 text-[13px] text-brand-muted">
          Sender, subject, date and a short excerpt, not full bodies or attachments.
        </p>
        <p className="mt-1 text-[13px] text-brand-muted">Nothing before you connected.</p>
        <p className="mt-3 text-[13px]">
          <Link
            to="/help/email-connections"
            className="text-brand-accent underline-offset-2 hover:underline"
          >
            How email connections work
          </Link>
        </p>
      </div>

    </div>
  );
}

function GmailConnectionRow() {
  const fetchConnection = useServerFn(getMyGmailConnection);
  const startConnect = useServerFn(startGmailConnect);
  const disconnect = useServerFn(disconnectGmail);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["my-gmail-connection"],
    queryFn: () => fetchConnection(),
  });
  const [error, setError] = React.useState<string | null>(null);
  const [connecting, setConnecting] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);

  async function onConnect() {
    setError(null);
    setConnecting(true);
    try {
      const { authorize_url } = await startConnect();
      const target = window.top ?? window;
      target.location.href = authorize_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the Gmail connection.");
      setConnecting(false);
    }
  }

  async function onConfirmDisconnect() {
    setError(null);
    setDisconnecting(true);
    try {
      await disconnect();
      await queryClient.invalidateQueries({ queryKey: ["my-gmail-connection"] });
      await queryClient.invalidateQueries({ queryKey: ["mailbox-readiness"] });
      setConfirmOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not disconnect Gmail.");
    } finally {
      setDisconnecting(false);
    }
  }

  const conn = query.data;

  return (
    <div>
      {query.isLoading ? (
        <div className="mt-4 rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center text-[14px] text-brand-muted">
          Loading…
        </div>
      ) : query.error ? (
        <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-4 text-[14px] text-destructive">
          {(query.error as Error).message}
        </div>
      ) : conn && conn.connected ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-brand-line bg-brand-paper px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-[14px] font-medium text-brand-ink">
              Gmail · {conn.account_email}
            </div>
            <div className="mt-0.5 text-[13px] text-brand-muted">
              Connected {formatConnectedDate(conn.connected_at)}
            </div>
            <div className="mt-1 text-[13px] text-brand-muted">
              Connect Gmail to send outreach from your own address. CapitalDart
              saves drafts to your Gmail Drafts folder, sends only when you press
              Send or give AgentDart permission, and reads replies to outreach you
              sent through CapitalDart so your pipeline updates itself. It does not
              read other mail. Reading replies to CapitalDart outreach. Disconnect to
              stop.
            </div>
            {conn.needs_reconnect && (
              <div className="mt-0.5 text-[13px] text-brand-muted">
                Reconnect Gmail to let AgentDart save drafts and read replies.
              </div>
            )}
            {!conn.calendar_ready && (
              <div className="mt-0.5 text-[13px] text-brand-muted">
                Reconnect Gmail to let AgentDart create Google Meet meetings.
              </div>
            )}

          </div>
          <div className="flex shrink-0 items-center gap-3">
            {(conn.needs_reconnect || !conn.calendar_ready) && (
              <Button size="sm" onClick={() => void onConnect()} disabled={connecting}>
                {connecting ? "Opening Google…" : "Reconnect"}
              </Button>
            )}
            <button
              type="button"
              onClick={() => {
                setError(null);
                setConfirmOpen(true);
              }}
              className="text-[14px] text-brand-accent underline-offset-2 hover:underline"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-brand-line bg-brand-paper px-4 py-4">
          <p className="text-[14px] text-brand-muted">
            Connect Gmail to send outreach from your own address. CapitalDart
            saves drafts to your Gmail Drafts folder, sends only when you press
            Send or give AgentDart permission, and reads replies to outreach you
            sent through CapitalDart so your pipeline updates itself. It does not
            read other mail.
          </p>
          <div className="mt-4">
            <Button onClick={() => void onConnect()} disabled={connecting}>
              {connecting ? "Opening Google…" : "Connect Gmail"}
            </Button>
            <p className="mt-2 text-[13px] text-brand-muted">
              Google shows a warning that this app is being tested. Choose Continue;
              only invited accounts can connect. Ask us to add your address if you
              are blocked.
            </p>
          </div>

        </div>
      )}
      {error && <p className="mt-3 text-[14px] text-destructive">{error}</p>}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open && !disconnecting) setConfirmOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Gmail?</AlertDialogTitle>
            <AlertDialogDescription>
              CapitalDart will no longer be able to send from this Gmail address.
              You can connect again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void onConfirmDisconnect();
              }}
              disabled={disconnecting}
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function PasswordChangeForm() {
  const [pw, setPw] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [status, setStatus] = React.useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    if (pw.length < 8) {
      setStatus({ kind: "err", msg: "Password must be at least 8 characters." });
      return;
    }
    if (pw !== confirm) {
      setStatus({ kind: "err", msg: "Passwords do not match." });
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) {
      setStatus({ kind: "err", msg: error.message });
      return;
    }
    setPw("");
    setConfirm("");
    setStatus({ kind: "ok", msg: "Password updated." });
  }

  return (
    <div>
      <PanelHeader title="Password" subtitle="Change the password you use to sign in." />
      <form
        onSubmit={onSubmit}
        className="mt-4 rounded-xl border border-brand-line bg-brand-paper px-4 py-1"
      >
        <Field
          label="New password"
          type="password"
          value={pw}
          onChange={(e) => {
            setPw(e.target.value);
            setStatus(null);
          }}
          autoComplete="new-password"
        />
        <Field
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            setStatus(null);
          }}
          autoComplete="new-password"
        />
        <StatusMessage status={status} />
        <div className="py-4">
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Updating…" : "Update password"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function SessionsList() {
  const fetchSessions = useServerFn(getMySessions);
  const revoke = useServerFn(revokeSession);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["my-sessions"],
    queryFn: () => fetchSessions(),
  });
  const [error, setError] = React.useState<string | null>(null);
  const [revoking, setRevoking] = React.useState<string | null>(null);

  async function onRevoke(s: SessionRow) {
    setError(null);
    setRevoking(s.id);
    try {
      await revoke({ data: { sessionId: s.id } });
      await queryClient.invalidateQueries({ queryKey: ["my-sessions"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign out session.");
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div>
      <PanelHeader
        title="Active sessions"
        subtitle="Devices currently signed into your account."
      />
      {query.isLoading ? (
        <div className="mt-4 rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center text-[14px] text-brand-muted">
          Loading…
        </div>
      ) : query.error ? (
        <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-4 text-[14px] text-destructive">
          {(query.error as Error).message}
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-brand-line overflow-hidden rounded-xl border border-brand-line bg-brand-paper">
          {(query.data ?? []).map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[14px] text-brand-ink">
                  <span className="truncate font-medium">
                    {parseUserAgent(s.user_agent)}
                  </span>
                  {s.is_current && (
                    <span className="rounded-full bg-brand-ink/10 px-2 py-0.5 text-[14px] font-medium uppercase tracking-wider text-brand-ink">
                      This device
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-[13px] text-brand-muted">
                  {s.ip ? `${s.ip} · ` : ""}Last active {relativeTime(s.updated_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRevoke(s)}
                disabled={s.is_current || revoking === s.id}
                className="shrink-0 text-[14px] text-brand-accent underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-brand-muted disabled:no-underline"
                title={s.is_current ? "Use the top-bar Sign out for this device" : undefined}
              >
                {revoking === s.id ? "Signing out…" : "Sign out"}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-3 text-[14px] text-destructive">{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────────────

function PanelHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <>
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.18em] text-brand-ink">
        {title}
      </h2>
      <p className="mt-1 text-[14px] text-brand-muted">{subtitle}</p>
    </>
  );
}

function formatConnectedDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ConnectedAssistantsList() {
  const fetchList = useServerFn(listConnectedAssistants);
  const revoke = useServerFn(revokeSession);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["my-connected-assistants"],
    queryFn: () => fetchList(),
  });
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<ConnectedAssistantRow | null>(null);
  const [revoking, setRevoking] = React.useState<string | null>(null);

  async function onConfirmRevoke() {
    if (!pending) return;
    setError(null);
    setRevoking(pending.session_id);
    try {
      await revoke({ data: { sessionId: pending.session_id } });
      await queryClient.invalidateQueries({ queryKey: ["my-connected-assistants"] });
      setPending(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke access.");
    } finally {
      setRevoking(null);
    }
  }

  const rows = query.data ?? [];

  return (
    <div>
      <PanelHeader
        title="Connected assistants"
        subtitle="AI assistants that can act on your CapitalDart account through the MCP connector."
      />
      {query.isLoading ? (
        <div className="mt-4 rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center text-[14px] text-brand-muted">
          Loading…
        </div>
      ) : query.error ? (
        <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-4 text-[14px] text-destructive">
          {(query.error as Error).message}
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center text-[14px] text-brand-muted">
          No assistants connected. See the{" "}
          <a href="/connect" className="underline">Connect page</a> to link an AI assistant.
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-brand-line overflow-hidden rounded-xl border border-brand-line bg-brand-paper">
          {rows.map((row) => {
            const lastActive = row.refreshed_at ?? row.created_at;
            return (
              <li
                key={row.session_id}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-medium text-brand-ink">
                    {row.client_name || "AI assistant"}
                  </div>
                  <div className="mt-0.5 text-[13px] text-brand-muted">
                    Connected {formatConnectedDate(row.created_at)} · Last active{" "}
                    {formatConnectedDate(lastActive)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setPending(row);
                  }}
                  disabled={revoking === row.session_id}
                  className="shrink-0 text-[14px] text-brand-accent underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-brand-muted disabled:no-underline"
                >
                  {revoking === row.session_id ? "Revoking…" : "Revoke"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {error && <p className="mt-3 text-[14px] text-destructive">{error}</p>}

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && revoking === null) setPending(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access?</AlertDialogTitle>
            <AlertDialogDescription>
              This assistant will lose access to your CapitalDart account immediately.
              You can reconnect it at any time from your assistant's settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void onConfirmRevoke();
              }}
              disabled={revoking !== null}
            >
              {revoking !== null ? "Revoking…" : "Revoke access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusMessage({
  status,
}: {
  status: { kind: "ok" | "err"; msg: string } | null;
}) {
  if (!status) return null;
  return (
    <div
      className={cn(
        "my-3 rounded-md border px-3 py-2 text-[14px]",
        status.kind === "ok"
          ? "border-brand-ok/30 bg-brand-ok/10 text-[oklch(0.45_0.087_155.7)]"
          : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
      role={status.kind === "err" ? "alert" : "status"}
    >
      {status.msg}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Email preferences
// ─────────────────────────────────────────────────────────────────────────────

function EmailPanel() {
  const queryClient = useQueryClient();
  const fetchPrefs = useServerFn(getMyEmailPreferences);
  const savePrefs = useServerFn(updateMyEmailPreferences);

  const prefsQuery = useQuery({
    queryKey: ["my-email-prefs"],
    queryFn: () => fetchPrefs(),
  });

  const mutation = useMutation({
    mutationFn: (data: { product_emails?: boolean; nudge_emails?: boolean }) =>
      savePrefs({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-email-prefs"] });
      track("account_settings_changed", { section: "email" });
    },
  });

  const prefs = prefsQuery.data;

  return (
    <section id="email-preferences">
      <PanelHeader
        title="Email preferences"
        subtitle="Choose which non-essential emails you receive. Transactional emails (run notifications, receipts, account changes) cannot be disabled."
      />
      <div className="mt-4 rounded-xl border border-brand-line bg-brand-paper px-4 py-3">
        {!prefs ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-brand-line py-3">
              <div>
                <div className="text-[14px] text-brand-ink">Product updates</div>
                <div className="text-[14px] text-brand-muted">
                  Low-balance and credit-depleted notices.
                </div>
              </div>
              <Switch
                checked={prefs.product_emails}
                onCheckedChange={(v) => mutation.mutate({ product_emails: v })}
                disabled={mutation.isPending}
              />
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="text-[14px] text-brand-ink">Reengagement</div>
                <div className="text-[14px] text-brand-muted">
                  Abandoned checkout and inactivity nudges.
                </div>
              </div>
              <Switch
                checked={prefs.nudge_emails}
                onCheckedChange={(v) => mutation.mutate({ nudge_emails: v })}
                disabled={mutation.isPending}
              />
            </div>
            <p className="border-t border-brand-line pt-3 text-[12px] text-brand-muted">
              Transactional emails cannot be disabled.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
